// turnState.ts — pure state derivations for the Sessions page's turns and
// layout, kept out of the hooks so they can be tested without React.
//
//   turnBusyId        which session has a turn in flight, as this tab sees it.
//   focusedSessionId  the session whose frames may write to the transcript.
//   shouldDrainQueue  whether a held message can be sent now.
//   clampPaneWidth    the sidebar width a divider drag lands on.
//   parsePaneWidth    a stored sidebar width, or the default.

export interface BusyState {
  /** The session this tab is streaming over its own response, if any. */
  sseStreamingId: string | null;
  /** The session this tab is mid-send to, until the turn starts. */
  submitting: string | null;
  /** The harness-reported status of the selected session. */
  selectedStatus: string;
  /** The session on screen. */
  selectedId: string | null;
}

// Three sources, because there are three moments a turn can be real
// without the socket having said so yet: the send this tab just made, a
// turn being streamed over a response because the socket is down, and —
// the ordinary case — the harness reporting one, including a turn started
// from another tab entirely.
export function turnBusyId(s: BusyState): string | null {
  return s.sseStreamingId
    ?? s.submitting
    ?? ((s.selectedStatus === 'running' || s.selectedStatus === 'cancelling')
      ? s.selectedId
      : null);
}

// The session whose frames may write to the transcript: the selected one,
// once its history is on screen. Everything else stays a sidebar row,
// however busy it is. '' means none.
export function focusedSessionId(historyLoadedId: string | null, selectedId: string | null): string {
  return historyLoadedId === selectedId ? (selectedId ?? '') : '';
}

export interface QueuedMessage {
  sessionId: string;
  content: string;
}

// A held message is sent once its pod is actually up and nothing else is
// running. The phase only tracks the selected session, so a message typed
// into one the user has since navigated away from waits until they come
// back rather than being sent blind.
export function shouldDrainQueue(
  queued: QueuedMessage | null,
  selectedId: string | null,
  phase: string | null,
  streaming: boolean,
): boolean {
  if (!queued || queued.sessionId !== selectedId) return false;
  return phase === 'ready' && !streaming;
}

/** The sidebar width before anyone drags the divider. */
export const DEFAULT_PANE_WIDTH = 280;
export const MIN_PANE_WIDTH = 160;
export const MAX_PANE_WIDTH = 640;

/** The width a drag that started at `startWidth` and moved `dx` lands on. */
export function clampPaneWidth(startWidth: number, dx: number): number {
  return Math.max(MIN_PANE_WIDTH, Math.min(MAX_PANE_WIDTH, startWidth + dx));
}

/**
 * A width read back from storage. Not clamped — a stored width is the
 * user's own choice and is honored as-is, matching the page before the
 * split; only drags clamp.
 */
export function parsePaneWidth(raw: string | null): number {
  const n = raw ? parseInt(raw, 10) : NaN;
  return Number.isFinite(n) ? n : DEFAULT_PANE_WIDTH;
}
